import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createContact, getApiErrorMessage } from '../../api-wa';

const inputStyle = {
  padding: '12px 14px',
  borderRadius: '12px',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-soft)',
  color: 'var(--text-dark)',
  outline: 'none',
  fontSize: '14.5px',
  width: '100%',
  maxWidth: '100%',
  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.06)',
  marginTop: '4px'
};

const actionStyle = {
  padding: '10px 14px',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
  background: 'var(--brand-purple)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '14px',
  boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
};

const secondaryButtonStyle = {
  padding: '10px 14px',
  borderRadius: '10px',
  border: '1px solid var(--border-color)',
  background: 'var(--bg-main)',
  color: 'var(--text-dark)', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  fontWeight: 700,
  fontSize: '14px',
  cursor: 'pointer',
};

const modalOverlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
};

const modalCardStyle = {
  width: 'min(540px, 100%)', borderRadius: '24px', background: 'var(--surface-white)',
  border: '1px solid var(--glass-border)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', padding: '32px', display: 'grid', gap: '20px',
};

const fieldLabelStyle = { fontSize: '14px', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '6px' };

const countryCodes = ['+91', '+1', '+44', '+61', '+971', '+65'];

export default function AddContactModal({ visible, onClose, onSaved }) {
  const [form, setForm] = useState({ name: '', phone_number: '', email: '', country_code: '+91' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setForm({ name: '', phone_number: '', email: '', country_code: '+91' });
      setError('');
    }
  }, [visible]);

  if (!visible) return null;

  const handleFieldChange = (field, value) => setForm((s) => ({ ...s, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.phone_number.trim()) {
      setError('Phone number is required.');
      return;
    }

    const cleanPhone = form.phone_number.trim().replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Phone number must be exactly 10 digits.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        name: form.name.trim() || undefined,
        phone_number: `${form.country_code}${form.phone_number.trim()}`,
        email: form.email.trim() || undefined,
        language: 'en',
      };

      const created = await createContact(payload);
      setSubmitting(false);
      onClose?.();
      if (typeof onSaved === 'function') onSaved(created);
    } catch (err) {
      setSubmitting(false);
      setError(getApiErrorMessage(err, 'Failed to create contact.'));
    }
  };

  return createPortal(
    <div className="wa-module">
      <div style={modalOverlayStyle} onClick={onClose}>
        <form style={modalCardStyle} onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong style={{ fontSize: '22px', color: 'var(--text-dark)' }}>Add New Contact</strong>
              <div style={{ fontSize: '13px', color: 'var(--text-gray)', marginTop: '2px' }}>Create a contact</div>
            </div>
            <button type="button" className="close-x" onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '18px', color: 'var(--text-gray)', cursor: 'pointer' }}>✕</button>
          </div>

          {error && <div style={{ color: '#b42318' }}>{error}</div>}

          {/* Phone Number Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: '12px' }}>
            <div style={fieldLabelStyle}>Phone Number</div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              background: 'var(--bg-main)', 
              border: '1px solid var(--border-color)', 
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-sm)',
              maxWidth: '220px'
            }}>
              <select 
                value={form.country_code} 
                onChange={(e) => handleFieldChange('country_code', e.target.value)} 
                style={{ 
                  border: 'none', 
                  background: 'transparent', 
                  padding: '8px 0 8px 10px', 
                  fontSize: '13px', 
                  fontWeight: '600', 
                  color: 'var(--brand-purple)',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                {countryCodes.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div style={{ width: '1px', height: '18px', background: 'rgba(0,0,0,0.1)', margin: '0 6px' }}></div>
              <input 
                style={{ 
                  border: 'none', 
                  background: 'transparent', 
                  padding: '8px 10px 8px 0', 
                  fontSize: '13px', 
                  flexGrow: 1, 
                  outline: 'none',
                  color: 'var(--text-dark, #111827)'
                }} 
                placeholder="10 digits" 
                value={form.phone_number} 
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  handleFieldChange('phone_number', val);
                }} 
              />
            </div>
          </div>

          {/* Contact Name Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: '12px' }}>
            <div style={fieldLabelStyle}>Contact Name</div>
            <input style={inputStyle} placeholder="Full name" value={form.name} onChange={(e) => handleFieldChange('name', e.target.value)} />
          </div>

          {/* Email Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', alignItems: 'center', gap: '12px' }}>
            <div style={fieldLabelStyle}>Email Address</div>
            <input style={inputStyle} placeholder="name@company.com" value={form.email} onChange={(e) => handleFieldChange('email', e.target.value)} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button type="button" onClick={onClose} style={secondaryButtonStyle}>Cancel</button>
            <button type="submit" disabled={submitting} style={actionStyle}>{submitting ? 'Saving...' : '+ Add New Contact'}</button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// Helper: parse CSV or Excel file content into contact objects
export async function parseSpreadsheetFile(file) {
  const name = (file && file.name || '').toLowerCase();
  if (name.endsWith('.csv') || file.type === 'text/csv') {
    const text = await file.text();
    return parseCsvTextToContacts(text);
  }

  // Try Excel formats
  const arrayBuffer = await file.arrayBuffer();
  const mod = await import('xlsx');
  const XLSX = mod && (mod.default ?? mod);
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const json = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

  // Normalize rows
  const rows = Array.isArray(json) ? json : [];
  return rows.map((row) => {
    // Find keys case-insensitively and ignore space/underscore differences
    const getVal = (possibleKeys) => {
      const foundKey = Object.keys(row).find(key => {
        const normKey = key.toLowerCase().replace(/[\s_-]/g, '');
        return possibleKeys.some(pk => pk.toLowerCase().replace(/[\s_-]/g, '') === normKey);
      });
      return foundKey ? row[foundKey] : '';
    };

    const nameVal = getVal(['name', 'contactname', 'fullname']);
    const phoneVal = getVal(['phone_number', 'phone', 'phonenumber']);
    const emailVal = getVal(['email', 'emailaddress']);

    return {
      name: String(nameVal).trim(),
      phone_number: String(phoneVal).trim(),
      email: String(emailVal).trim(),
      language: 'en_US',
    };
  }).filter(r => r.phone_number);
}

export function validateBulkContactRows(rows) {
  const normalizedRows = Array.isArray(rows) ? rows : [];

  const validContactTypes = new Set(['', 'HOT', 'WARM', 'COLD']);
  const invalidRows = [];
  const seenNumbers = new Set();

  const cleanedRows = normalizedRows.map((row, index) => {
    const name = String(row.name || '').trim();
    const rawPhone = String(row.phone_number || row.phone || '').trim();
    // Normalize phone for duplicate detection (digits only)
    const normalizedPhone = rawPhone.replace(/\D/g, '');
    const email = String(row.email || '').trim();

    const errors = [];
    if (!rawPhone) errors.push('missing phone number');
    if (rawPhone && !/^\+?[0-9][0-9\s()-]{5,}$/.test(rawPhone)) errors.push('invalid phone number format');
    
    if (normalizedPhone) {
      if (seenNumbers.has(normalizedPhone)) {
        errors.push(`duplicate phone number in file (${rawPhone})`);
      } else {
        seenNumbers.add(normalizedPhone);
      }
    }

    if (email && !/^\S+@\S+\.\S+$/.test(email)) errors.push('invalid email');

    if (errors.length > 0) {
      invalidRows.push({ row: index + 1, errors });
    }

    return {
      name,
      phone_number: rawPhone,
      email,
      language: String(row.language || 'en').trim() || 'en',
    };
  });

  return {
    cleanedRows,
    invalidRows,
    isValid: invalidRows.length === 0,
  };
}

function parseCsvTextToContacts(text) {
  const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  
  // Robust header mapping
  const rawHeaders = lines[0].split(',').map(h => h.trim().toLowerCase());
  const headerMap = {};
  rawHeaders.forEach((h, i) => {
    if (h.includes('name')) headerMap.name = i;
    else if (h.includes('phone')) headerMap.phone = i;
    else if (h.includes('email')) headerMap.email = i;
    else if (h.includes('lang')) headerMap.language = i;
    else if (h.includes('lang')) headerMap.language = i;
  });

  return lines.slice(1).map(line => {
    // Better CSV splitting that handles spaces and optionally quoted values
    // This splits by comma but preserves spaces within values
    const values = line.split(',').map(v => v.trim());
    const cleanValues = values.map(v => v.replace(/^"|"$/g, '').trim());
    
    const getVal = (key) => (headerMap[key] !== undefined ? cleanValues[headerMap[key]] : '');

    return {
      name: getVal('name'),
      phone_number: getVal('phone'),
      email: getVal('email'),
      language: getVal('language') || 'en',
    };
  }).filter(r => (r.name || r.phone_number) && r.phone_number);
}





